import React, { Component } from 'react'
import {
    Alert, Button, DatePicker, Descriptions, Drawer, Form, Input, InputNumber, message, Modal,
    Select, Space, Table, Tag, Tooltip, Typography
} from 'antd'
import {
    CopyOutlined, FileTextOutlined, PlusOutlined, ReloadOutlined, StopOutlined, UndoOutlined
} from '@ant-design/icons'
import {
    cancelCodexTask,
    createCodexTask,
    getCodexTaskAutomationPrompt,
    getCodexTaskDetail,
    getCodexTaskList,
    getUserListForDropbox,
    requeueCodexTaskDelivery,
} from '../../request'

const { Option } = Select
const { Paragraph, Text } = Typography
const { TextArea } = Input

const TASK_STATUS_META = {
    PUBLISHED: { text: '执行中', color: 'processing' },
    COMPLETED: { text: '已结束', color: 'success' },
    CANCELLED: { text: '已取消', color: 'default' },
}

const DELIVERY_STATUS_META = {
    PENDING: { text: '待领取', color: 'default' },
    RUNNING: { text: '执行中', color: 'processing' },
    SUCCEEDED: { text: '成功', color: 'success' },
    FAILED: { text: '失败', color: 'error' },
    CANCELLED: { text: '已取消', color: 'default' },
}

export default class CodexTaskManagement extends Component {
    createFormRef = React.createRef()

    state = {
        loading: false,
        dataSource: [],
        pagination: { current: 1, pageSize: 20, total: 0 },
        keyword: '',
        status: undefined,
        filterUserId: undefined,
        users: [],
        createVisible: false,
        createSubmitting: false,
        createRequestId: '',
        detailVisible: false,
        detailLoading: false,
        detail: null,
        promptVisible: false,
        promptLoading: false,
        promptText: '',
        clientBaseUrl: '',
        actionDeliveryId: null,
    }

    componentDidMount() {
        this.loadList()
        this.loadUsers()
    }

    loadUsers = () => {
        getUserListForDropbox({ type: 0 })
            .then(users => this.setState({ users: users || [] }))
            .catch(() => {})
    }

    loadList = (pageNum = this.state.pagination.current, pageSize = this.state.pagination.pageSize) => {
        const { keyword, status, filterUserId } = this.state
        this.setState({ loading: true })
        return getCodexTaskList({
            pageNum,
            pageSize,
            keyword: keyword ? keyword.trim() : undefined,
            status,
            userId: filterUserId,
        }).then(page => {
            const result = page || {}
            this.setState({
                dataSource: result.list || [],
                pagination: {
                    current: result.pageNum || pageNum,
                    pageSize: result.pageSize || pageSize,
                    total: result.total || 0,
                },
            })
        }).catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    openCreate = () => {
        this.setState({
            createVisible: true,
            createRequestId: this.generateRequestId(),
        }, () => {
            if (this.createFormRef.current) {
                this.createFormRef.current.resetFields()
                this.createFormRef.current.setFieldsValue({ priority: 0 })
            }
        })
    }

    submitCreate = () => {
        this.createFormRef.current.validateFields().then(values => {
            this.setState({ createSubmitting: true })
            const expireTime = values.expireTime ? values.expireTime.format('YYYY-MM-DD HH:mm:ss') : undefined
            return createCodexTask({
                requestId: this.state.createRequestId,
                title: values.title,
                taskContent: values.taskContent,
                userIds: values.userIds,
                priority: values.priority || 0,
                expireTime,
            }).then(result => {
                message.success(result && result.reused ? '任务已存在，已返回原任务' : '任务已发布')
                this.setState({ createVisible: false })
                this.loadList(1, this.state.pagination.pageSize)
            }).catch(() => {})
                .finally(() => this.setState({ createSubmitting: false }))
        }).catch(() => {})
    }

    openDetail = (record) => {
        this.setState({
            detailVisible: true,
            detailLoading: true,
            detail: null,
        })
        getCodexTaskDetail({ taskId: record.id })
            .then(detail => this.setState({ detail }))
            .catch(() => {})
            .finally(() => this.setState({ detailLoading: false }))
    }

    reloadDetail = () => {
        const { detail } = this.state
        if (detail && detail.id) {
            this.openDetail(detail)
        }
    }

    confirmCancel = (record) => {
        Modal.confirm({
            title: '确认取消任务？',
            content: '取消后，尚未领取的用户将不再执行；已经领取的 Codex 不会被强制中断。',
            okText: '确认取消',
            okButtonProps: { danger: true },
            onOk: () => cancelCodexTask({ taskId: record.id }).then(() => {
                message.success('任务已取消')
                this.loadList()
                if (this.state.detail && String(this.state.detail.id) === String(record.id)) {
                    this.reloadDetail()
                }
            }),
        })
    }

    confirmRequeue = (delivery) => {
        const isRunning = delivery.status === 'RUNNING'
        Modal.confirm({
            title: '确认重新入队？',
            content: isRunning
                ? '该记录仍在执行中，重新入队可能造成同一任务重复执行。请先确认原执行器已停止。'
                : `将为 ${delivery.targetUserName || delivery.targetUserId} 重新开放领取。`,
            okText: '重新入队',
            okButtonProps: isRunning ? { danger: true } : {},
            onOk: () => {
                this.setState({ actionDeliveryId: delivery.id })
                return requeueCodexTaskDelivery({ deliveryId: delivery.id }).then(() => {
                    message.success('已重新入队')
                    this.reloadDetail()
                    this.loadList()
                }).finally(() => this.setState({ actionDeliveryId: null }))
            },
        })
    }

    openPrompt = () => {
        this.setState({
            promptVisible: true,
            promptLoading: true,
            promptText: '',
            clientBaseUrl: '',
        })
        getCodexTaskAutomationPrompt()
            .then(result => this.setState({
                promptText: result && result.prompt ? result.prompt : '',
                clientBaseUrl: result && result.clientBaseUrl ? result.clientBaseUrl : '',
            }))
            .catch(() => {})
            .finally(() => this.setState({ promptLoading: false }))
    }

    copyText = (text) => {
        if (!text) {
            message.warning('当前没有可复制内容')
            return
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text)
                .then(() => message.success('已复制'))
                .catch(() => message.warning('复制失败，请手动选择复制'))
            return
        }
        message.warning('当前浏览器不支持自动复制，请手动选择复制')
    }

    generateRequestId = () => {
        return `codex-task-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    }

    formatUserName = (user) => {
        return user.USER_NICK || user.userNick || user.USER_NAME || user.userName || user.USER_ID || user.userId
    }

    getUserId = (user) => {
        return user.USER_ID || user.userId
    }

    renderTaskStatus = (status) => {
        const meta = TASK_STATUS_META[status] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderDeliveryStatus = (status) => {
        const meta = DELIVERY_STATUS_META[status] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderProgress = (record) => (
        <Space size={[4, 4]} wrap>
            <Tooltip title="待领取"><Tag>{record.pendingCount || 0} 待领</Tag></Tooltip>
            <Tooltip title="执行中"><Tag color="processing">{record.runningCount || 0} 执行</Tag></Tooltip>
            <Tooltip title="成功"><Tag color="success">{record.succeededCount || 0} 成功</Tag></Tooltip>
            <Tooltip title="失败"><Tag color="error">{record.failedCount || 0} 失败</Tag></Tooltip>
            {Number(record.cancelledCount || 0) > 0 ? <Tag>{record.cancelledCount} 取消</Tag> : null}
        </Space>
    )

    columns = [
        {
            title: '任务',
            key: 'task',
            width: 300,
            render: (_, record) => (
                <div>
                    <Text strong>{record.title}</Text>
                    <Paragraph
                        type="secondary"
                        ellipsis={{ rows: 2 }}
                        style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}
                    >
                        {record.taskContent}
                    </Paragraph>
                </div>
            ),
        },
        {
            title: '接收人',
            dataIndex: 'targetUserNames',
            width: 190,
            render: value => (
                <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                    {value || '-'}
                </Paragraph>
            ),
        },
        {
            title: '状态 / 进度',
            key: 'progress',
            width: 300,
            render: (_, record) => (
                <Space direction="vertical" size={6}>
                    {this.renderTaskStatus(record.status)}
                    {this.renderProgress(record)}
                </Space>
            ),
        },
        {
            title: '调度',
            key: 'schedule',
            width: 150,
            render: (_, record) => (
                <div>
                    <div>优先级：{record.priority || 0}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        过期：{record.expireTime || '不限制'}
                    </Text>
                </div>
            ),
        },
        {
            title: '发布信息',
            key: 'creator',
            width: 180,
            render: (_, record) => (
                <div>
                    <div>{record.createdByUserName || record.createdByUserId || '-'}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.createTime || '-'}</Text>
                </div>
            ),
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button size="small" type="link" onClick={() => this.openDetail(record)}>详情</Button>
                    {record.status === 'PUBLISHED' ? (
                        <Button
                            size="small"
                            type="link"
                            danger
                            icon={<StopOutlined />}
                            onClick={() => this.confirmCancel(record)}
                        >
                            取消
                        </Button>
                    ) : null}
                </Space>
            ),
        },
    ]

    deliveryColumns = [
        {
            title: '接收人',
            key: 'user',
            width: 160,
            render: (_, record) => record.targetUserName || record.targetUserId || '-',
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: this.renderDeliveryStatus,
        },
        {
            title: '执行器 / 次数',
            key: 'executor',
            width: 180,
            render: (_, record) => (
                <div>
                    <div>{record.executorId || '-'}</div>
                    <Text type="secondary">领取 {record.attemptCount || 0} 次</Text>
                </div>
            ),
        },
        {
            title: '时间',
            key: 'time',
            width: 210,
            render: (_, record) => (
                <div>
                    <div>领取：{record.claimedAt || '-'}</div>
                    <div>完成：{record.finishedAt || '-'}</div>
                    {record.leaseExpiresAt ? <div>租约：{record.leaseExpiresAt}</div> : null}
                </div>
            ),
        },
        {
            title: '执行结果',
            key: 'result',
            render: (_, record) => (
                <div>
                    <Paragraph ellipsis={{ rows: 3 }} style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                        {record.resultSummary || '-'}
                    </Paragraph>
                    {record.errorSummary ? (
                        <Paragraph type="danger" ellipsis={{ rows: 3 }} style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                            {record.errorSummary}
                        </Paragraph>
                    ) : null}
                </div>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 110,
            render: (_, record) => ['FAILED', 'RUNNING', 'CANCELLED'].includes(record.status) ? (
                <Button
                    size="small"
                    icon={<UndoOutlined />}
                    loading={this.state.actionDeliveryId === record.id}
                    onClick={() => this.confirmRequeue(record)}
                >
                    重新入队
                </Button>
            ) : null,
        },
    ]

    renderCreateModal = () => {
        const { createVisible, createSubmitting, users } = this.state
        return (
            <Modal
                title="发布 Codex 任务"
                visible={createVisible}
                width={760}
                okText="发布任务"
                cancelText="取消"
                confirmLoading={createSubmitting}
                onOk={this.submitCreate}
                onCancel={() => this.setState({ createVisible: false })}
                destroyOnClose
            >
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 20 }}
                    message="任务会在接收人的 Codex 定时任务下次运行时被领取"
                    description="任务内容将直接交给 Codex 执行，请写清目标、作用范围、验收标准和需要人工确认的边界，不要填写密码或 API Key。"
                />
                <Form ref={this.createFormRef} layout="vertical" initialValues={{ priority: 0 }}>
                    <Form.Item
                        name="title"
                        label="任务标题"
                        rules={[{ required: true, message: '请填写任务标题' }, { max: 200 }]}
                    >
                        <Input placeholder="例如：更新公司内部 DWS Skill" maxLength={200} />
                    </Form.Item>
                    <Form.Item
                        name="userIds"
                        label="接收用户"
                        rules={[{ required: true, message: '至少选择一个接收用户' }]}
                    >
                        <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="children"
                            placeholder="选择需要执行任务的同事"
                            maxTagCount={6}
                        >
                            {users.map(user => (
                                <Option key={this.getUserId(user)} value={this.getUserId(user)}>
                                    {this.formatUserName(user)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="taskContent"
                        label="任务内容"
                        rules={[{ required: true, message: '请填写任务内容' }, { max: 20000 }]}
                    >
                        <TextArea
                            rows={11}
                            maxLength={20000}
                            showCount
                            placeholder={'建议包含：\n1. 要完成的目标\n2. 允许修改的目录或系统\n3. 验收方式\n4. 失败时如何处理\n5. 哪些操作必须等待人工确认'}
                        />
                    </Form.Item>
                    <Space size={24} align="start">
                        <Form.Item name="priority" label="优先级（0-9）">
                            <InputNumber min={0} max={9} precision={0} />
                        </Form.Item>
                        <Form.Item name="expireTime" label="过期时间（可选）">
                            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        )
    }

    renderDetailDrawer = () => {
        const { detailVisible, detailLoading, detail } = this.state
        return (
            <Drawer
                title="任务详情"
                visible={detailVisible}
                width="82%"
                onClose={() => this.setState({ detailVisible: false })}
                extra={<Button icon={<ReloadOutlined />} onClick={this.reloadDetail}>刷新</Button>}
            >
                {detail ? (
                    <>
                        <Descriptions bordered size="small" column={3}>
                            <Descriptions.Item label="标题" span={2}>{detail.title}</Descriptions.Item>
                            <Descriptions.Item label="状态">{this.renderTaskStatus(detail.status)}</Descriptions.Item>
                            <Descriptions.Item label="发布人">{detail.createdByUserName || detail.createdByUserId}</Descriptions.Item>
                            <Descriptions.Item label="发布时间">{detail.createTime || '-'}</Descriptions.Item>
                            <Descriptions.Item label="优先级 / 过期">{detail.priority || 0} / {detail.expireTime || '不限制'}</Descriptions.Item>
                            <Descriptions.Item label="幂等ID" span={3}>
                                <Text code copyable>{detail.requestId}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="任务内容" span={3}>
                                <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                                    {detail.taskContent}
                                </Paragraph>
                            </Descriptions.Item>
                        </Descriptions>
                        <Table
                            style={{ marginTop: 24 }}
                            rowKey="id"
                            loading={detailLoading}
                            columns={this.deliveryColumns}
                            dataSource={detail.deliveries || []}
                            pagination={false}
                            scroll={{ x: 1150 }}
                        />
                    </>
                ) : (
                    <Table loading={detailLoading} columns={[]} dataSource={[]} pagination={false} />
                )}
            </Drawer>
        )
    }

    renderPromptModal = () => {
        const { promptVisible, promptLoading, promptText, clientBaseUrl } = this.state
        return (
            <Modal
                title="创建 Codex 每小时定时任务"
                visible={promptVisible}
                width={820}
                footer={[
                    <Button key="close" onClick={() => this.setState({ promptVisible: false })}>关闭</Button>,
                    <Button
                        key="copy"
                        type="primary"
                        icon={<CopyOutlined />}
                        disabled={!promptText}
                        onClick={() => this.copyText(promptText)}
                    >
                        复制创建指令
                    </Button>,
                ]}
                onCancel={() => this.setState({ promptVisible: false })}
            >
                <Alert
                    type="info"
                    showIcon
                    message="复制整段内容，并发送到一个普通 Codex 对话"
                    description={`Codex 会自动创建或更新每小时运行的 Scheduled Task，请不要把整段创建指令直接保存成任务内容。${clientBaseUrl ? ` 领取接口：${clientBaseUrl}` : ''}`}
                    style={{ marginBottom: 16 }}
                />
                <TextArea
                    value={promptText}
                    placeholder={promptLoading ? '正在加载提示词...' : ''}
                    readOnly
                    rows={20}
                    style={{ fontFamily: 'monospace' }}
                />
            </Modal>
        )
    }

    render() {
        const {
            loading, dataSource, pagination, keyword, status, filterUserId, users,
        } = this.state
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    type="info"
                    showIcon
                    message="Codex 任务下发"
                    description="管理员发布任务后，目标用户电脑上的 Codex 定时任务会按优先级领取。系统不会自动重跑超时任务，失败或异常记录需人工确认后重新入队。"
                    style={{ marginBottom: 20 }}
                />
                <Space size={12} wrap style={{ marginBottom: 16 }}>
                    <Input.Search
                        allowClear
                        value={keyword}
                        placeholder="搜索标题或任务内容"
                        style={{ width: 260 }}
                        onChange={event => this.setState({ keyword: event.target.value })}
                        onSearch={() => this.loadList(1, pagination.pageSize)}
                    />
                    <Select
                        allowClear
                        value={status}
                        placeholder="任务状态"
                        style={{ width: 140 }}
                        onChange={value => this.setState({ status: value }, () => this.loadList(1, pagination.pageSize))}
                    >
                        {Object.keys(TASK_STATUS_META).map(value => (
                            <Option key={value} value={value}>{TASK_STATUS_META[value].text}</Option>
                        ))}
                    </Select>
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        value={filterUserId}
                        placeholder="接收用户"
                        style={{ width: 220 }}
                        onChange={value => this.setState({ filterUserId: value }, () => this.loadList(1, pagination.pageSize))}
                    >
                        {users.map(user => (
                            <Option key={this.getUserId(user)} value={this.getUserId(user)}>
                                {this.formatUserName(user)}
                            </Option>
                        ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={() => this.loadList()}>刷新</Button>
                    <Button icon={<FileTextOutlined />} onClick={this.openPrompt}>创建定时任务</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreate}>发布任务</Button>
                </Space>
                <Table
                    rowKey="id"
                    loading={loading}
                    columns={this.columns}
                    dataSource={dataSource}
                    pagination={pagination}
                    scroll={{ x: 1270 }}
                    onChange={next => this.loadList(next.current, next.pageSize)}
                />
                {this.renderCreateModal()}
                {this.renderDetailDrawer()}
                {this.renderPromptModal()}
            </div>
        )
    }
}
